<script>
import { h } from "vue";
/** Used to add spacing between a collection of items with flexbox. Props allow to change spacing size, axis, and distribution.
 * @version 1.5.0
 */
export default {
  name: "MksSpacer",
  props: {
    /** Set alignment on secondary axis. (align-items) */
    align: {
      type: String,
      default: "start",
      validator: value => {
        return value.match(/(start|end|center|stretch|baseline)/);
      }
    },
    /** Apply same spacing to nested elements */
    deep: {
      type: Boolean,
      default: false
    },
    /** Set alignment on main axis (justify-content) */
    distribute: {
      type: String,
      default: "start",
      validator: value => {
        return value.match(/(start|end|center|equal|spaced)/);
      }
    },
    /** Elements flow on the vertical axis by default, inline makes them flow horizontally */
    inline: {
      type: Boolean,
      default: false
    },
    /** Set spacing between elements */
    spacing: {
      type: String,
      default: "m",
      validator: value => {
        return value.match(/(xxxs|xxs|xs|s|m|l|xl|xxl|xxxl)/);
      }
    },
    /** Tag of the wrapper element */
    tag: {
      type: String,
      default: "div"
    },
    /** Elements should wrap if they extend size of container */
    nowrap: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classes() {
      let classes = "spacer";
      classes += ` spacer--spacing-${this.spacing}`;
      classes += ` spacer--align-${this.align}`;
      classes += ` spacer--distribute-${this.distribute}`;
      if (this.nowrap) classes += " spacer--no-wrap";
      if (this.inline) {
        classes += " spacer--inline";
      } else {
        classes += " spacer--stack";
      }
      return classes;
    }
  },
  render(oldH) {
    let createComponent = h;
    if (typeof h !== "function" && typeof oldH === "function") {
      createComponent = oldH;
    }
    const childs = [];
    const defaultSlot =
      typeof this.$slots.default === "function"
        ? this.$slots.default()
        : this.$slots.default;
    (defaultSlot || []).forEach((item) => {
      // verifier si pas de tag comment faire (node)
      // const text = item.text && item.text.trim();
      if (item) {
        const itemType = item.type && item.type.toString();
        if (item.type && item.type.name === "MksSpacerItem") {
          childs.push(item);
        } else if (itemType !== "Symbol(Comment)") {
          if (
            itemType === "Symbol(Fragment)" ||
            itemType === "Symbol()" ||
            itemType === "template" ||
            itemType.match(/^Symbol\(/)
          ) {
            if (Array.isArray(item.children) && item.children.length) {
              item.children.forEach(child => {
                if (child.children === " ") {
                  return;
                }
                childs.push(h("div", { class: "spacer__item" }, [child]));
              });
            }
            return;
          }
          if (item.children === " ") {
            return;
          }
          childs.push(
            createComponent("div", { class: "spacer__item" }, [item])
          );
        } else {
          const text = item.text && item.text.trim();

          if (item && (text || item.tag)) {
            if (
              item.componentOptions &&
              item.componentOptions.tag === "mks-spacer-item"
            ) {
              childs.push(item);
            } else {
              childs.push(
                createComponent("div", { class: "spacer__item" }, [item])
              );
            }
          }
        }
      }
    });
    return createComponent(this.tag, { class: this.classes }, childs);
  }
};
</script>
<style lang="scss">
$sizes: ("xxxs", "xxs", "xs", "s", "m", "l", "xl", "xxl", "xxxl");
@each $size in $sizes {
  .spacer--spacing-#{$size} {
    --spacer-container-space: var(--space-#{$size});
    margin-top: calc(-1 * var(--space-#{$size}));
    margin-left: calc(-1 * var(--space-#{$size}));

    & > * {
      // apply on children to prevent overriding of first element when nesting mks-spacer components
      --spacer-space: var(--space-#{$size});
    }
  }
}

.spacer > .spacer {
  margin-top: calc(var(--spacer-space) - var(--spacer-container-space));
  margin-left: calc(var(--spacer-space) - var(--spacer-container-space));
  width: 100%;

  &--inline {
    height: 100%;
    width: auto;
  }
}

.spacer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  margin-top: calc(-1 * var(--spacer-container-space));
  margin-left: calc(-1 * var(--spacer-container-space));
  max-width: 100%;

  & > * {
    margin-top: var(--spacer-space);
    margin-left: var(--spacer-space);
    flex: 0 0 auto;
    min-width: 0;
    max-width: 100%;
  }

  &--stack > .spacer__item {
    width: 100%;
  }

  &--inline {
    flex-direction: row;
    align-items: center;
    max-height: 100%;
    max-width: none;
  }

  &--no-wrap {
    flex-wrap: nowrap;
  }

  // ALIGN
  &--align-start {
    align-items: flex-start;
  }

  &--align-end {
    align-items: flex-end;
  }

  &--align-center {
    align-items: center;
  }

  &--align-stretch {
    align-items: stretch;
  }

  &--align-baseline {
    align-items: baseline;
  }

  // DISTRIBUTE
  &--distribute-start {
    justify-content: flex-start;
  }

  &--distribute-end {
    justify-content: flex-end;
  }

  &--distribute-center {
    justify-content: center;
  }

  &--distribute-equal {
    justify-content: space-between;
  }

  &--distribute-spaced {
    justify-content: space-around;
  }
}
</style>

